import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { eur, eur2, pct } from "@/lib/format";
import { PageHeader, DecisionBadge, Field } from "@/components/shared";
import { DECISION } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, CheckCircle2, XCircle, Save, TrendingDown } from "lucide-react";

const ROWS = [
  ["purchase_price", "Prezzo di acquisto"],
  ["all_in_costs", "Costi complessivi (all-in)"],
];

export default function Evaluation() {
  const [params] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [settings, setSettings] = useState(null);
  const [propertyId, setPropertyId] = useState("");
  const [form, setForm] = useState({ purchase_price: 0, expected_sale_price: 0, all_in_costs: 0, duration_months: 4 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const selectProperty = useCallback((pid) => {
    setPropertyId(pid);
    setResult(null);
    const p = properties.find((x) => x.id === pid);
    if (p) setForm({ purchase_price: p.proposed_price || 0, expected_sale_price: 0, all_in_costs: p.renovation_cost || 0, duration_months: 4 });
  }, [properties]);

  useEffect(() => {
    const pid = params.get("property");
    if (pid && properties.length) { selectProperty(pid); return; }
    const purchase = params.get("purchase");
    if (purchase) setForm((f) => ({ ...f, purchase_price: Number(purchase) || 0 }));
  }, [properties, params, selectProperty]);

  const num = (v) => Number(v) || 0;
  const run = async (save = false) => {
    setBusy(true);
    try {
      const p = properties.find((x) => x.id === propertyId);
      const payload = {
        property_id: propertyId, property_label: p ? p.address : "",
        purchase_price: num(form.purchase_price), expected_sale_price: num(form.expected_sale_price),
        all_in_costs: num(form.all_in_costs), duration_months: num(form.duration_months),
      };
      const { data } = await api.post(`/evaluate?save=${save}`, payload);
      setResult(data);
      if (save) toast.success("Valutazione salvata sull'immobile");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="evaluation-page">
      <PageHeader title="Valutazione Acquisizione" subtitle="Test di calcolo · Strategia: rivendita dopo ristrutturazione" />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <Card className="p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" />Parametri dell'operazione</h3>
          <div className="space-y-4">
            <Field label="Immobile">
              <Select value={propertyId} onValueChange={selectProperty}>
                <SelectTrigger data-testid="eval-property-select"><SelectValue placeholder="Seleziona un immobile" /></SelectTrigger>
                <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Prezzo di acquisto (€)"><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="eval-purchase" /></Field>
              <Field label="Prezzo di rivendita (€)"><Input type="number" value={form.expected_sale_price} onChange={(e) => setForm({ ...form, expected_sale_price: e.target.value })} data-testid="eval-sale" /></Field>
              <Field label="Costi complessivi operazione (€) — all-in"><Input type="number" value={form.all_in_costs} onChange={(e) => setForm({ ...form, all_in_costs: e.target.value })} data-testid="eval-allin" /></Field>
              <Field label="Durata (mesi)"><Input type="number" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} data-testid="eval-duration" /></Field>
              <p className="col-span-2 text-xs text-muted-foreground">L'importo all-in comprende tutte le spese oltre al prezzo d'acquisto: lavori, imposte, notaio, tecnici/pratiche, mantenimento, eventuale finanziamento, costi di vendita e imprevisti. Il costo totale è calcolato come acquisto + all-in.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button className="flex-1" onClick={() => run(false)} disabled={busy} data-testid="run-eval-btn"><Calculator className="h-4 w-4 mr-2" />Calcola</Button>
              <Button variant="outline" onClick={() => run(true)} disabled={busy || !propertyId} data-testid="save-eval-btn"><Save className="h-4 w-4 mr-2" />Salva</Button>
            </div>
          </div>

          {settings && (
            <div className="mt-6 pt-4 border-t border-border">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Assunzioni aziendali</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted-foreground">
                <span>Costo max: <b className="text-slate-700">{eur(settings.max_total_cost)}</b></span>
                <span>Costo target: <b className="text-slate-700">{eur(settings.target_total_cost)}</b></span>
                <span>Utile minimo: <b className="text-slate-700">{eur(settings.min_profit)}</b></span>
                <span>Durata max: <b className="text-slate-700">{settings.max_duration_months} mesi</b></span>
                <span>Stress vendita: <b className="text-slate-700">{settings.sale_stress_pct}%</b></span>
                <span>Stress costi all-in: <b className="text-slate-700">+{settings.renovation_stress_pct}%</b></span>
              </div>
            </div>
          )}
        </Card>

        {/* Result */}
        <div className="space-y-6">
          {!result && <Card className="p-10 grid place-items-center text-center text-muted-foreground min-h-[300px]"><div><Calculator className="h-10 w-10 mx-auto mb-3 text-slate-300" />Inserisci i parametri e premi <b>Calcola</b> per vedere l'esito.</div></Card>}

          {result && (
            <>
              <Card className={`p-6 border-l-4 ${DECISION[result.decision].border}`} data-testid="eval-result">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Esito</div>
                    <div className="mt-1"><DecisionBadge decision={result.decision} className="text-sm px-3 py-1.5" /></div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl font-bold text-slate-900">{pct(result.base.roi)}</div>
                    <div className="text-xs text-muted-foreground">ROI stimato</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-border">
                  <div><div className="text-xs text-muted-foreground">Costo totale</div><div className="font-mono font-semibold text-slate-800">{eur(result.base.total_cost)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Rivendita</div><div className="font-mono font-semibold text-slate-800">{eur(result.base.sale_price)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Utile</div><div className={`font-mono font-semibold ${result.base.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{eur(result.base.profit)}</div></div>
                </div>
                {result.decision === "NEGOTIATE" && (
                  <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                    Prezzo di acquisto massimo consigliato: <b>{eur(result.suggested_purchase_price)}</b>
                  </div>
                )}
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-800 mb-3">Dettaglio Calcolo</h3>
                <div className="space-y-1.5">
                  {ROWS.map(([k, label]) => (
                    <div key={k} className="flex justify-between text-sm py-1 border-b border-border/60 last:border-0">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-mono text-slate-800">{eur2(result.base[k])}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm py-2 font-semibold">
                    <span>Costo totale</span><span className="font-mono text-primary">{eur2(result.base.total_cost)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground pt-2">Costo totale = Prezzo di acquisto + Costi complessivi (all-in). Le voci incluse (lavori, imposte, notaio, tecnici/pratiche, mantenimento, finanziamento, costi di vendita, imprevisti) sono già comprese nell'importo all-in e non vengono sommate una seconda volta.</p>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><TrendingDown className="h-4 w-4 text-amber-500" />Stress Test</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Vendita {result.stress_assumptions.sale_stress_pct}% · Costi all-in +{result.stress_assumptions.renovation_stress_pct}%
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div><div className="text-xs text-muted-foreground">Costo stressato</div><div className="font-mono font-semibold text-slate-800">{eur(result.stressed.total_cost)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Utile stressato</div><div className={`font-mono font-semibold ${result.stressed.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{eur(result.stressed.profit)}</div></div>
                  <div><div className="text-xs text-muted-foreground">ROI stressato</div><div className="font-mono font-semibold text-slate-800">{pct(result.stressed.roi)}</div></div>
                </div>
                <div className={`mt-3 inline-flex items-center gap-1.5 text-sm font-medium ${result.checks.stress_ok ? "text-emerald-600" : "text-rose-600"}`}>
                  {result.checks.stress_ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {result.checks.stress_ok ? "Stress test superato" : "Stress test fallito"}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-slate-800 mb-3">Motivazioni</h3>
                <ul className="space-y-2">
                  {result.reasons.map((r, i) => <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-primary">•</span>{r}</li>)}
                </ul>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
